import { useSyncExternalStore, useMemo, useCallback, useEffect, useState } from "react";
import { getCollab, type CollabEntry } from "./collab";

export function useCollab(noteId: string, user: { id: string; displayName: string }, epoch = 0) {
  const entry = useMemo(() => getCollab(noteId, user, epoch), [noteId, user.id, epoch]); // eslint-disable-line react-hooks/exhaustive-deps
  return useEntryState(entry);
}

/** Re-render when an entry's sync state changes. */
export function useEntryState<T extends CollabEntry>(entry: T): T {
  const subscribe = useCallback(
    (cb: () => void) => {
      entry.listeners.add(cb);
      return () => entry.listeners.delete(cb);
    },
    [entry],
  );
  const snapshot = useSyncExternalStore(subscribe, () => `${entry.state}|${entry.lastSavedAt}|${entry.error}|${entry.everSynced}|${entry.fromOfflineCopy}`);
  void snapshot;
  return entry;
}

/** Live text of a collaborative note (re-renders on every change, throttled by animation frames). */
export function useLiveText(entry: CollabEntry) {
  const ytext = entry.doc.getText("content");
  const [text, setText] = useState(() => ytext.toString());
  useEffect(() => {
    let raf = 0;
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setText(ytext.toString()));
    };
    ytext.observe(on);
    setText(ytext.toString());
    return () => {
      cancelAnimationFrame(raf);
      ytext.unobserve(on);
    };
  }, [ytext]);
  return text;
}

export interface PresenceUser {
  clientId: number;
  name: string;
  color: string;
  id: string;
}

export function usePresence(entry: CollabEntry): PresenceUser[] {
  const awareness = entry.awareness;
  const [users, setUsers] = useState<PresenceUser[]>([]);
  useEffect(() => {
    if (!awareness) return;
    const update = () => {
      const list: PresenceUser[] = [];
      awareness.getStates().forEach((state: any, clientId: number) => {
        if (clientId === awareness.clientID || !state?.user) return;
        list.push({ clientId, name: state.user.name, color: state.user.color, id: state.user.id });
      });
      setUsers(list);
    };
    awareness.on("change", update);
    update();
    return () => awareness.off("change", update);
  }, [awareness]);
  return users;
}
